// @ts-nocheck

import React, { useCallback, useContext, useEffect, ChangeEvent } from "react";
import { Controller } from "react-hook-form";
import Cleave from "cleave.js/react";
import cn from "classnames";
import Skeleton from "react-loading-skeleton";

import { FormContext } from "../../Form";

import Icon from "../../../brand/Icon";
import LabelTextItem from "../../LabelTextItem";
import Label from "../Label";
import FieldMessage from "../FieldMessage";

import { determinePotentialPlaceholder } from "./DateField.utils";
import { DateFieldProps } from "./DateField.props";

import styles from "./DateField.module.scss";

const DateField: React.FC<DateFieldProps> = ({
  id,
  labelText,
  placeholder,
  delimiter,
  dateComponents,
  onChange,
  tabIndex,
  disabled = false,
  inputClassName,
  containerClassName,
  helperText,
  name,
  validate,
  autocomplete = true,
  icon,
  useAsterisk,
  isLoading,
  readOnly = false,
  displayReadOnlyFieldInline = false,
}: DateFieldProps): JSX.Element => {
  const {
    control,
    setValue,
    register,
    unregister,
    readOnly: readOnlyForm,
    formState: { errors, isFormLoading },
  } = useContext(FormContext);

  const hasErrors = disabled ? undefined : errors;
  const isInvalid = errors && !disabled ? Object.keys(errors)?.length && errors[name] : false;

  const hasValidation = disabled ? undefined : validate;
  const registerField = register(name, hasValidation);

  const inputContainerClasses = cn(styles["form-field-container"], { disabled: disabled }, containerClassName);
  const inputClasses = cn(styles["text-field"], inputClassName, { [styles["invalid-field"]]: isInvalid });

  const handleInputChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      setValue(name, ev.target.value);

      registerField.onChange(ev);

      if (onChange) {
        onChange(ev);
      }
    },
    [name, onChange, registerField],
  );

  // removes validation requirements on a disabled field
  useEffect(() => {
    if (disabled) {
      unregister(name);
    }
  }, [name, disabled]);

  const IconComponent = useCallback(() => {
    return icon ? <Icon id={id} name={icon} size="small" color="black" /> : null;
  }, [id, icon]);

  let component;

  if (isLoading || isFormLoading) {
    component = <Skeleton height={40} />;
  } else {
    const datePattern = [];

    if (dateComponents?.length) {
      datePattern.push(...dateComponents);
    } else {
      datePattern.push(...["m", "d", "Y"]);
    }

    const options = {
      date: true,
      delimiter: delimiter ?? "/",
      datePattern,
    };

    const defaultPlaceholder = determinePotentialPlaceholder(datePattern);

    component = (
      <Controller
        name={name}
        rules={validate}
        control={control}
        render={({ field: { value } }): JSX.Element => {
          let component;

          if (readOnly || readOnlyForm) {
            const readOnlyValue = value ?? defaultPlaceholder;

            component = (
              <LabelTextItem
                id={id}
                label={labelText}
                displayInline={displayReadOnlyFieldInline}
                body={readOnlyValue}
                isLoading={isLoading || isFormLoading}
              />
            );
          } else {
            component = (
              <>
                <IconComponent />
                <Cleave
                  {...registerField}
                  data-testid={`test__${id}`}
                  aria-invalid={isInvalid ? "true" : "false"}
                  aria-label={labelText}
                  id={id}
                  className={inputClasses}
                  placeholder={placeholder ?? defaultPlaceholder}
                  tabIndex={tabIndex}
                  disabled={disabled}
                  onChange={handleInputChange}
                  onBlur={handleInputChange}
                  autoComplete={autocomplete ? "on" : "off"}
                  value={value}
                  options={options}
                />
              </>
            );
          }

          return (
            <>
              {labelText && (
                <Label
                  id={id}
                  label={labelText}
                  disabled={disabled}
                  isLoading={isLoading || isFormLoading}
                  validate={validate}
                  useAsterisk={useAsterisk}
                />
              )}
              {component}
              {!(readOnly || readOnlyForm) && (
                <FieldMessage id={id} helperText={helperText} errors={hasErrors} name={name} isLoading={isLoading || isFormLoading} />
              )}
            </>
          );
        }}
      />
    );
  }

  return <div className={inputContainerClasses}>{component}</div>;
};

export default DateField;
